import { useEffect, useState, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { UsersRound, Plus, Pencil, Trash2, Shield, Copy, CheckCircle2, Search, Loader2, UserPlus, X, Users, ChevronRight, Mail, UserCircle, Filter, Building2, Globe } from "lucide-react";
import MemberAccessDialog, { SOCIETE_ROLE_LABELS } from "../components/team/MemberAccessDialog";
import { useSociete } from "../contexts/SocieteContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import PageHeader from "../components/PageHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { PortalDropdown } from "../components/PortalDropdown";
import { ACCESS_TYPES, BUSINESS_ROLES, SPECIALTIES } from "../constants/roleConstants";
import HelpIcon from "../components/help/HelpIcon";
import MemberHoverCard from "../components/team/MemberHoverCard";

const ROLE_LABELS = { admin: "Admin", coach: "Coach", sous_traitant: "Sous-traitant" };
const ROLE_COLORS = { admin: "#f7c935", coach: "#3b82f6", sous_traitant: "#10b981" };
const SPECIALTY_LABELS = { developpeur: "Developpeur", coach_freelance: "Coach", comptable: "Comptable" };

const PERM_LABELS = {
  manage_clients: "Gerer les clients", delete_clients: "Supprimer des clients",
  manage_services: "Gerer les services", delete_services: "Supprimer des services",
  manage_audits: "Gerer les audits", delete_audits: "Supprimer des audits",
  manage_formations: "Gerer les formations", delete_formations: "Supprimer des formations",
  manage_accompagnements: "Gerer les accompagnements", delete_accompagnements: "Supprimer des accompagnements",
  manage_notes: "Gerer les notes", delete_notes: "Supprimer des notes",
  manage_documents: "Gerer les documents", delete_documents: "Supprimer des documents",
  manage_agenda: "Gerer l'agenda",
  manage_equipements: "Gerer les equipements",
  manage_depenses: "Gerer les depenses",
  manage_encaissements: "Gerer les encaissements",
  access_tresorerie: "Acceder a la tresorerie",
  manage_qualiopi: "Gerer Qualiopi",
  manage_formation_inscriptions: "Gerer les inscriptions formation",
  view_formation_bpf: "Voir les statistiques BPF",
  manage_formation_compliance: "Audit conformite Qualiopi",
  access_fec: "Acces FEC", access_finances: "Acces Finances", access_builder: "Acces Builder",
  manage_staff: "Gerer le personnel", manage_permissions: "Gerer les permissions",
  view_all_clients: "Voir tous les clients",
};

const inputCls = "bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935]";

export default function TeamPage() {
  const { axiosAuth, user } = useAuth();
  const { currentSociete } = useSociete();
  const [accessMember, setAccessMember] = useState(null);
  const canManageTeam = user?.role === "superadmin" || user?.group_user || user?.societe_role_active === "admin";
  const navigate = useNavigate();
  const api = axiosAuth();
  const [staff, setStaff] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [filterAccess, setFilterAccess] = useState("all"); // type d'acces (admin/superadmin/user/developpeur)
  const [filterBusiness, setFilterBusiness] = useState("all"); // role metier (formateur/stagiaire/...)

  // Create dialog
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", email: "", password: "", role: "admin", specialty: "", phone: "", societe_role: "member" });
  const [creating, setCreating] = useState(false);

  // Invite dialog
  const [showInvite, setShowInvite] = useState(false);
  const [invForm, setInvForm] = useState({ name: "", email: "", role: "user", specialty: "", business_roles: [] });
  const [invLink, setInvLink] = useState("");
  const [copied, setCopied] = useState(false);

  // Permissions dialog
  const [showPerms, setShowPerms] = useState(false);
  const [permsUser, setPermsUser] = useState(null);
  const [permsData, setPermsData] = useState({});
  const [savingPerms, setSavingPerms] = useState(false);

  // Assign dialog
  const [showAssign, setShowAssign] = useState(false);
  const [assignStaffId, setAssignStaffId] = useState("");
  const [assignClientId, setAssignClientId] = useState("");

  // Invitations
  const [staffInvitations, setStaffInvitations] = useState([]);
  const [clientInvitations, setClientInvitations] = useState([]);
  const [teamTab, setTeamTab] = useState("members");

  const load = async () => {
    try {
      const [s, c, si, ci] = await Promise.all([
        api.get("/admin/staff"),
        api.get("/admin/clients"),
        api.get("/admin/staff-invitations"),
        api.get("/admin/invitations"),
      ]);
      setStaff(s.data);
      setClients(c.data);
      setStaffInvitations(si.data);
      setClientInvitations(ci.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    setCreating(true);
    try {
      await api.post("/admin/staff", createForm);
      setShowCreate(false);
      setCreateForm({ name: "", email: "", password: "", role: "admin", specialty: "", phone: "" });
      load();
    } catch (e) { console.error(e); }
    setCreating(false);
  };

  const handleInvite = async () => {
    try {
      const r = await api.post("/admin/invite-staff", invForm);
      setInvLink(`${window.location.origin}/login?staff_invite=${r.data.invite_token}`);
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (userId) => {
    await api.delete(`/admin/staff/${userId}`);
    load();
  };

  const openPerms = async (member) => {
    setPermsUser(member);
    try {
      const r = await api.get(`/admin/permissions/${member.user_id}`);
      setPermsData(r.data.permissions || {});
    } catch { setPermsData(member.permissions || {}); }
    setShowPerms(true);
  };

  const savePerms = async () => {
    setSavingPerms(true);
    try {
      await api.put(`/admin/permissions/${permsUser.user_id}`, { permissions: permsData });
      setShowPerms(false);
      load();
    } catch (e) { console.error(e); }
    setSavingPerms(false);
  };

  const handleAssign = async () => {
    try {
      await api.post("/admin/assignments", { client_id: assignClientId, staff_id: assignStaffId });
      setShowAssign(false);
      setAssignClientId("");
      setAssignStaffId("");
      load();
    } catch (e) { console.error(e); }
  };

  const handleRemoveAssignment = async (staffId, clientId) => {
    const assignments = await api.get(`/admin/assignments/${clientId}`);
    const asgn = assignments.data.find(a => a.staff_id === staffId);
    if (asgn) {
      await api.delete(`/admin/assignments/${asgn.assignment_id}`);
      load();
    }
  };

  const clientName = (cid) => {
    const c = clients.find(cl => cl.user_id === cid);
    return c ? (c.name || c.email) : cid;
  };

  const cancelStaffInvitation = async (invId) => {
    try { await api.delete(`/admin/staff-invitations/${invId}`); load(); } catch (e) { console.error(e); }
  };

  const cancelClientInvitation = async (invId) => {
    try { await api.delete(`/admin/invitations/${invId}`); load(); } catch (e) { console.error(e); }
  };

  const pendingStaffInvs = staffInvitations.filter(i => i.status === "pending");
  const pendingClientInvs = clientInvitations.filter(i => i.status === "pending");
  const allPendingInvs = [
    ...pendingStaffInvs.map(i => ({ ...i, inv_type: "staff" })),
    ...pendingClientInvs.map(i => ({ ...i, inv_type: "client" })),
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  if (loading) return <div className="p-8 text-slate-400">Chargement...</div>;

  const filtered = staff.filter(s => {
    if (filterRole !== "all" && s.role !== filterRole) return false;
    if (filterAccess !== "all" && s.role !== filterAccess) return false;
    if (filterBusiness !== "all") {
      const businessRoles = s.roles || [];
      if (!businessRoles.includes(filterBusiness)) return false;
    }
    return (s.name || s.email || "").toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div className="p-8 max-w-6xl" data-testid="team-page">
      <div className="flex items-start gap-2 mb-2">
        <PageHeader
          icon={<UsersRound />}
          title="Mon équipe"
          description="Gérez votre équipe, les rôles, permissions et invitations"
          testId="team-header"
          actions={(
            <>
              <Button onClick={() => setShowAssign(true)} variant="outline" className="border-white/10 text-white hover:bg-white/5 font-bold" data-testid="assign-btn">
                <Users className="w-4 h-4 mr-2" /> Attribuer
              </Button>
              <Button onClick={() => { setInvLink(""); setInvForm({ name: "", email: "", role: "user", specialty: "", business_roles: [] }); setShowInvite(true); }} variant="outline" className="border-white/10 text-white hover:bg-white/5 font-bold" data-testid="invite-staff-btn">
                <UserPlus className="w-4 h-4 mr-2" /> Inviter
              </Button>
              {canManageTeam && (<>              <Button onClick={() => setShowCreate(true)} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="create-staff-btn">
                <Plus className="w-4 h-4 mr-2" /> Créer
              </Button>
              </>)}
            </>
          )}
        />
        <div className="pt-3"><HelpIcon topic="team_management" size="md" /></div>
      </div>

      <Tabs value={teamTab} onValueChange={setTeamTab}>
        <TabsList className="bg-[#14101c] border border-white/10 mb-6">
          <TabsTrigger value="members" className="data-[state=active]:bg-[#f7c935] data-[state=active]:text-[#14101c] gap-2" data-testid="team-tab-members">
            <UsersRound className="w-4 h-4" /> Membres ({filtered.length})
          </TabsTrigger>
          <TabsTrigger value="invitations" className="data-[state=active]:bg-[#f7c935] data-[state=active]:text-[#14101c] gap-2" data-testid="team-tab-invitations">
            <Mail className="w-4 h-4" /> Invitations ({allPendingInvs.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="members">
          {/* Filter — replaced les bulles jaunes par 2 dropdowns clairs */}
          <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input placeholder="Rechercher..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-[#0b0811] border-white/10 text-white placeholder:text-slate-500 focus:border-[#f7c935]" data-testid="team-search-input" />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-500 flex-shrink-0" />
              <Select value={filterAccess} onValueChange={setFilterAccess}>
                <SelectTrigger className="bg-[#0b0811] border-white/10 text-white text-xs min-w-[160px]" data-testid="filter-access-select">
                  <SelectValue placeholder="Type d'acces" />
                </SelectTrigger>
                <SelectContent className="bg-[#14101c] border-white/10 text-white">
                  <SelectItem value="all">Tous les types d'acces</SelectItem>
                  {ACCESS_TYPES.map(a => (
                    <SelectItem key={a.value} value={a.value}>
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full" style={{ background: a.color }} />
                        {a.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterBusiness} onValueChange={setFilterBusiness}>
                <SelectTrigger className="bg-[#0b0811] border-white/10 text-white text-xs min-w-[160px]" data-testid="filter-business-select">
                  <SelectValue placeholder="Role metier" />
                </SelectTrigger>
                <SelectContent className="bg-[#14101c] border-white/10 text-white">
                  <SelectItem value="all">Tous les roles metier</SelectItem>
                  {BUSINESS_ROLES.map(b => (
                    <SelectItem key={b.value} value={b.value}>
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full" style={{ background: b.color }} />
                        {b.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(filterAccess !== "all" || filterBusiness !== "all" || search) && (
                <button
                  onClick={() => { setFilterAccess("all"); setFilterBusiness("all"); setSearch(""); setFilterRole("all"); }}
                  className="text-[10px] px-2 py-1 rounded text-slate-400 hover:text-white hover:bg-white/5 transition-colors"
                  data-testid="filter-reset-btn"
                  title="Reinitialiser les filtres"
                >Reinit.</button>
              )}
            </div>
          </div>

          {/* Staff List */}
          <div className="space-y-3">
            {filtered.length === 0 ? (
              <div className="bg-[#14101c] border border-white/10 rounded-xl p-16 text-center">
                <UsersRound className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                <p className="text-slate-500">Aucun membre trouve</p>
              </div>
            ) : filtered.map((member, i) => (
              <MemberHoverCard key={member.user_id} userId={member.user_id} disabled={member.user_id === user?.user_id}>
              <div
                className="bg-[#14101c] border border-white/10 rounded-xl p-5 animate-fade-in-up group cursor-pointer hover:border-cyan-400/30 hover:bg-[#1a1626] transition-colors"
                style={{ animationDelay: `${i * 0.03}s` }}
                data-testid={`staff-${member.user_id}`}
                onClick={() => navigate(`/admin/staff/${member.user_id}/profile`)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-11 h-11 rounded-full flex items-center justify-center text-sm font-bold" style={{ background: "#461d8a", color: ROLE_COLORS[member.role] }}>
                      {member.name?.[0]?.toUpperCase() || "?"}
                    </div>
                    <div onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold">{member.name}</span>
                        <RoleSelector member={member} api={api} currentUser={user} onUpdated={load} />
                        {member.group_access && member.role !== "superadmin" && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#f7c935]/10 text-[#f7c935] font-bold flex items-center gap-1" data-testid={`group-access-${member.user_id}`}><Globe className="w-3 h-3" /> Toutes sociétés</span>}
                        {member.societe_role && member.role !== "superadmin" && !member.group_access && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold" style={{ color: SOCIETE_ROLE_LABELS[member.societe_role]?.color, background: `${SOCIETE_ROLE_LABELS[member.societe_role]?.color}1a` }} data-testid={`societe-role-${member.user_id}`}>
                            {SOCIETE_ROLE_LABELS[member.societe_role]?.label}{member.societes_count > 1 ? ` · ${member.societes_count} sociétés` : ""}
                          </span>
                        )}
                        <AdditionalRolesPicker member={member} api={api} onUpdated={load} />
                        {member.specialty && <span className="text-xs px-2 py-0.5 rounded-full bg-white/5 text-slate-400">{SPECIALTY_LABELS[member.specialty] || member.specialty}</span>}
                      </div>
                      <div className="text-sm text-slate-500">{member.email} {member.phone && `- ${member.phone}`}</div>
                      {/* Profile preview */}
                      {member.staff_profile && (member.staff_profile.skills || member.staff_profile.contract_type || member.staff_profile.daily_rate) && (
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          {member.staff_profile.contract_type && (
                            <span className="text-xs px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400">{member.staff_profile.contract_type}</span>
                          )}
                          {member.staff_profile.daily_rate && (
                            <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400">{member.staff_profile.daily_rate} EUR/j</span>
                          )}
                          {member.staff_profile.skills && (
                            <span className="text-xs text-slate-500 truncate max-w-[200px]">{member.staff_profile.skills}</span>
                          )}
                        </div>
                      )}
                      {member.assigned_clients?.length > 0 && (
                        <div className="flex items-center gap-1 mt-1 flex-wrap">
                          <span className="text-xs text-slate-600">Clients:</span>
                          {member.assigned_clients.slice(0, 5).map(cid => (
                            <span key={cid} className="text-xs px-1.5 py-0.5 rounded bg-[#461d8a]/20 text-[#5a2d9c]">{clientName(cid)}</span>
                          ))}
                          {member.assigned_clients.length > 5 && <span className="text-xs text-slate-600">+{member.assigned_clients.length - 5}</span>}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-cyan-400 font-bold flex items-center gap-1 px-2 py-1 rounded bg-cyan-500/10" data-testid={`view-profile-badge-${member.user_id}`}>
                      Voir le profil →
                    </span>
                    {canManageTeam && <button onClick={(e) => { e.stopPropagation(); setAccessMember(member); }} className="p-1.5 rounded hover:bg-[#f7c935]/10" data-testid={`access-${member.user_id}`} title="Sociétés & autorisations">
                      <Building2 className="w-4 h-4 text-[#f7c935]" />
                    </button>}
                    <button onClick={(e) => { e.stopPropagation(); openPerms(member); }} className="p-1.5 rounded hover:bg-white/10" data-testid={`perms-${member.user_id}`} title="Permissions globales">
                      <Shield className="w-4 h-4 text-slate-400" />
                    </button>
                    {canManageTeam && <button onClick={(e) => { e.stopPropagation(); handleDelete(member.user_id); }} className="p-1.5 rounded hover:bg-red-500/10" title="Supprimer">
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </button>}
                  </div>
                </div>
              </div>
              </MemberHoverCard>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="invitations">
          <div className="space-y-3" data-testid="invitations-list">
            {allPendingInvs.length === 0 ? (
              <div className="bg-[#14101c] border border-white/10 rounded-xl p-16 text-center">
                <Mail className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                <p className="text-slate-500">Aucune invitation en attente</p>
              </div>
            ) : allPendingInvs.map((inv, i) => (
              <div key={inv.invitation_id} className="bg-[#14101c] border border-white/10 rounded-xl p-5 flex items-center justify-between animate-fade-in-up" style={{ animationDelay: `${i * 0.03}s` }} data-testid={`inv-item-${inv.invitation_id}`}>
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 rounded-full flex items-center justify-center text-sm font-bold" style={{ background: inv.inv_type === "staff" ? "#3b82f620" : "#8b5cf620", color: inv.inv_type === "staff" ? "#3b82f6" : "#8b5cf6" }}>
                    {inv.inv_type === "staff" ? <Shield className="w-5 h-5" /> : <UserPlus className="w-5 h-5" />}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{inv.name || inv.email}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${inv.inv_type === "staff" ? "bg-blue-500/10 text-blue-400" : "bg-purple-500/10 text-purple-400"}`}>
                        {inv.inv_type === "staff" ? (ROLE_LABELS[inv.role] || inv.role || "Staff") : "Client"}
                      </span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-500/10 text-yellow-400">En attente</span>
                    </div>
                    <div className="text-sm text-slate-500">{inv.email} - Invite le {new Date(inv.created_at).toLocaleDateString("fr-FR")}</div>
                  </div>
                </div>
                <button
                  onClick={() => inv.inv_type === "staff" ? cancelStaffInvitation(inv.invitation_id) : cancelClientInvitation(inv.invitation_id)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-red-400 hover:bg-red-500/10 transition-colors"
                  data-testid={`cancel-inv-${inv.invitation_id}`}
                >
                  <X className="w-4 h-4" /> Annuler
                </button>
              </div>
            ))}
          </div>

          {/* Show accepted/past invitations */}
          {(staffInvitations.filter(i => i.status !== "pending").length > 0 || clientInvitations.filter(i => i.status !== "pending").length > 0) && (
            <div className="mt-8">
              <h3 className="text-sm font-bold text-slate-500 mb-3">Invitations passees</h3>
              <div className="space-y-2">
                {[
                  ...staffInvitations.filter(i => i.status !== "pending").map(i => ({ ...i, inv_type: "staff" })),
                  ...clientInvitations.filter(i => i.status !== "pending").map(i => ({ ...i, inv_type: "client" })),
                ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 10).map(inv => (
                  <div key={inv.invitation_id} className="bg-[#14101c] border border-white/5 rounded-lg p-3 flex items-center justify-between opacity-60">
                    <div className="flex items-center gap-3">
                      <span className="text-sm">{inv.name || inv.email}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${inv.inv_type === "staff" ? "bg-blue-500/10 text-blue-400" : "bg-purple-500/10 text-purple-400"}`}>
                        {inv.inv_type === "staff" ? "Staff" : "Client"}
                      </span>
                    </div>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/10 text-green-400">
                      {inv.status === "accepted" ? "Acceptee" : inv.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <MemberAccessDialog open={!!accessMember} onClose={() => setAccessMember(null)} api={api} member={accessMember} currentUser={user} onChanged={load} />

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-lg">
          <DialogHeader><DialogTitle className="text-xl font-bold">Creer un membre</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <Input placeholder="Nom complet" value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} className={inputCls} data-testid="staff-name" />
            <Input placeholder="Email" type="email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} className={inputCls} data-testid="staff-email" />
            <Input placeholder="Mot de passe" type="password" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} className={inputCls} />
            <div className="grid grid-cols-2 gap-3">
              <Select value={createForm.role} onValueChange={(v) => setCreateForm({ ...createForm, role: v })}>
                <SelectTrigger className={inputCls} data-testid="staff-role-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#14101c] border-white/10 text-white">
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
              {createForm.role === "sous_traitant" && (
                <Select value={createForm.specialty} onValueChange={(v) => setCreateForm({ ...createForm, specialty: v })}>
                  <SelectTrigger className={inputCls}><SelectValue placeholder="Specialite" /></SelectTrigger>
                  <SelectContent className="bg-[#14101c] border-white/10 text-white">
                    <SelectItem value="developpeur">Developpeur</SelectItem>
                    <SelectItem value="coach_freelance">Coach</SelectItem>
                    <SelectItem value="comptable">Comptable</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
            <Input placeholder="Telephone (optionnel)" value={createForm.phone} onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })} className={inputCls} />
            <div className="rounded-lg border border-white/10 bg-[#0b0811] p-3 space-y-2" data-testid="create-societe-scope">
              <div className="text-xs text-slate-400 flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" /> Société : <b className="text-slate-200">{currentSociete?.name || "— sélectionnez une société active —"}</b></div>
              <div className="flex gap-2">
                {[["admin", "Admin société"], ["member", "Membre"], ["readonly", "Lecture seule"]].map(([k, l]) => (
                  <button key={k} type="button" onClick={() => setCreateForm({ ...createForm, societe_role: k })} className={`text-xs px-2.5 py-1 rounded-full border ${createForm.societe_role === k ? "border-[#f7c935] text-[#f7c935] bg-[#f7c935]/10" : "border-white/10 text-slate-400"}`} data-testid={`create-societe-role-${k}`}>{l}</button>
                ))}
              </div>
              <p className="text-[10px] text-slate-500">Le membre n'aura accès qu'à cette société. Vous pourrez l'autoriser sur d'autres sociétés ensuite (bouton <Building2 className="w-3 h-3 inline" />).</p>
            </div>
            <Button onClick={handleCreate} disabled={creating || !createForm.name || !createForm.email} className="w-full bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="submit-create-staff">
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />} Creer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invite Dialog */}
      <Dialog open={showInvite} onOpenChange={(v) => { setShowInvite(v); if (!v) setInvLink(""); }}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-lg">
          <DialogHeader><DialogTitle className="text-xl font-bold">Inviter un membre</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            {!invLink ? (<>
              <p className="text-sm text-slate-400">Le membre pourra creer son compte via email/mot de passe ou Google OAuth.</p>
              <Input placeholder="Email" value={invForm.email} onChange={(e) => setInvForm({ ...invForm, email: e.target.value })} className={inputCls} data-testid="invite-staff-email" />
              <Input placeholder="Nom" value={invForm.name} onChange={(e) => setInvForm({ ...invForm, name: e.target.value })} className={inputCls} data-testid="invite-staff-name" />

              {/* Type d'acces */}
              <div>
                <label className="text-[10px] uppercase text-slate-400 font-bold tracking-wider mb-1.5 block">Type d'acces (privileges techniques)</label>
                <Select value={invForm.role} onValueChange={(v) => setInvForm({ ...invForm, role: v })}>
                  <SelectTrigger className={inputCls} data-testid="invite-access-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#14101c] border-white/10 text-white">
                    {ACCESS_TYPES.filter(a => a.value !== "superadmin" || user?.role === "superadmin").map(a => (
                      <SelectItem key={a.value} value={a.value} data-testid={`invite-access-option-${a.value}`}>
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full" style={{ background: a.color }} />
                          <span>{a.label}</span>
                          <span className="text-[10px] text-slate-500 ml-1">— {a.desc}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Roles metier (multi-select) */}
              <div>
                <label className="text-[10px] uppercase text-slate-400 font-bold tracking-wider mb-1.5 block">Roles metier (espaces fonctionnels accessibles)</label>
                <div className="grid grid-cols-2 gap-1.5" data-testid="invite-business-roles">
                  {BUSINESS_ROLES.map(b => {
                    const selected = (invForm.business_roles || []).includes(b.value);
                    return (
                      <button
                        key={b.value}
                        type="button"
                        onClick={() => {
                          const cur = invForm.business_roles || [];
                          const next = selected ? cur.filter(r => r !== b.value) : [...cur, b.value];
                          setInvForm({ ...invForm, business_roles: next });
                        }}
                        className={`text-left px-3 py-2 rounded-lg border transition-all ${selected ? "bg-[#0b0811]" : "bg-[#0b0811]/40 hover:bg-[#0b0811]"}`}
                        style={{ borderColor: selected ? b.color : "rgba(255,255,255,0.05)" }}
                        data-testid={`invite-business-role-${b.value}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className={`w-3 h-3 rounded ${selected ? "" : "border border-white/15"}`} style={{ background: selected ? b.color : "transparent" }}>
                            {selected && <CheckCircle2 className="w-3 h-3 text-white" />}
                          </span>
                          <span className="text-xs font-bold" style={{ color: selected ? b.color : "#cbd5e1" }}>{b.label}</span>
                        </div>
                        <div className="text-[10px] text-slate-500 mt-0.5 ml-5">{b.desc}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Specialite (uniquement si access type sous_traitant — legacy) */}
              {invForm.role === "sous_traitant" && (
                <div>
                  <label className="text-[10px] uppercase text-slate-400 font-bold tracking-wider mb-1.5 block">Specialite</label>
                  <Select value={invForm.specialty} onValueChange={(v) => setInvForm({ ...invForm, specialty: v })}>
                    <SelectTrigger className={inputCls}><SelectValue placeholder="Choisir une specialite" /></SelectTrigger>
                    <SelectContent className="bg-[#14101c] border-white/10 text-white">
                      {SPECIALTIES.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <Button onClick={handleInvite} disabled={!invForm.email} className="w-full bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="confirm-invite-btn">
                <UserPlus className="w-4 h-4 mr-2" /> Generer le lien d'invitation
              </Button>
            </>) : (
              <div>
                <p className="text-sm text-green-400 mb-3 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Invitation creee !</p>
                <div className="flex items-center gap-2">
                  <Input value={invLink} readOnly className="bg-[#0b0811] border-white/10 text-white text-xs" />
                  <Button onClick={() => { navigator.clipboard.writeText(invLink); setCopied(true); setTimeout(() => setCopied(false), 2000); }} size="sm" variant="outline" className="border-white/10 text-white hover:bg-white/5 flex-shrink-0">
                    {copied ? <CheckCircle2 className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Permissions Dialog */}
      <Dialog open={showPerms} onOpenChange={setShowPerms}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Shield className="w-5 h-5" style={{ color: "#f7c935" }} />
              Permissions - {permsUser?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {[
              { group: "Force de vente", perms: ["manage_clients", "delete_clients", "view_all_clients", "manage_notes", "delete_notes"] },
              { group: "Prestations", perms: ["manage_services", "delete_services", "manage_audits", "delete_audits", "manage_formations", "delete_formations", "manage_formation_inscriptions", "view_formation_bpf", "manage_formation_compliance", "manage_accompagnements", "delete_accompagnements"] },
              { group: "Gestion de l'activite", perms: ["manage_depenses", "manage_encaissements", "access_tresorerie", "manage_equipements", "manage_documents", "delete_documents", "manage_qualiopi", "manage_agenda"] },
              { group: "Administration", perms: ["manage_staff", "manage_permissions", "access_fec", "access_finances", "access_builder"] },
            ].map(section => (
              <div key={section.group}>
                <div className="text-[10px] uppercase font-bold tracking-wider mb-2 px-1" style={{ color: "#f7c935" }}>{section.group}</div>
                <div className="space-y-1">
                  {section.perms.filter(k => PERM_LABELS[k]).map(key => (
                    <button key={key} onClick={() => setPermsData({ ...permsData, [key]: !permsData[key] })}
                      className={`w-full flex items-center justify-between p-2.5 rounded-lg border text-sm transition-colors ${permsData[key] ? "bg-green-500/5 border-green-500/20 text-green-400" : "bg-[#0b0811] border-white/5 text-slate-500"}`}
                      data-testid={`perm-${key}`}>
                      <span>{PERM_LABELS[key]}</span>
                      <div className={`w-9 h-5 rounded-full transition-colors relative ${permsData[key] ? "bg-green-500" : "bg-white/10"}`}>
                        <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${permsData[key] ? "left-4" : "left-0.5"}`} />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <Button onClick={savePerms} disabled={savingPerms} className="w-full bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold mt-4" data-testid="save-perms-btn">
              {savingPerms ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
              Enregistrer les permissions
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog open={showAssign} onOpenChange={setShowAssign}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-lg">
          <DialogHeader><DialogTitle className="text-xl font-bold">Attribuer un client</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm text-slate-400 mb-1.5 block">Membre de l'equipe</label>
              <Select value={assignStaffId} onValueChange={setAssignStaffId}>
                <SelectTrigger className={inputCls} data-testid="assign-staff-select"><SelectValue placeholder="Choisir un membre..." /></SelectTrigger>
                <SelectContent className="bg-[#14101c] border-white/10 text-white max-h-60">
                  {staff.map(s => (
                    <SelectItem key={s.user_id} value={s.user_id}>
                      <div className="flex items-center gap-2">
                        <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: `${ROLE_COLORS[s.role]}15`, color: ROLE_COLORS[s.role] }}>{ROLE_LABELS[s.role]}</span>
                        {s.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-slate-400 mb-1.5 block">Client</label>
              <Select value={assignClientId} onValueChange={setAssignClientId}>
                <SelectTrigger className={inputCls} data-testid="assign-client-select"><SelectValue placeholder="Choisir un client..." /></SelectTrigger>
                <SelectContent className="bg-[#14101c] border-white/10 text-white max-h-60">
                  {clients.map(c => <SelectItem key={c.user_id} value={c.user_id}>{c.name || c.email} {c.company_name ? `(${c.company_name})` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleAssign} disabled={!assignStaffId || !assignClientId} className="w-full bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="submit-assign">
              <Users className="w-4 h-4 mr-2" /> Attribuer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─────────── INLINE ROLE SELECTOR ────────────────────────────
// Roles principaux : seulement admin, superadmin, user, developpeur
const ROLE_OPTIONS = [
  { value: "superadmin", label: "Master Admin", color: "#f7c935" },
  { value: "admin", label: "Admin", color: "#3b82f6" },
];

function RoleSelector({ member, api, currentUser, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const triggerRef = useRef(null);

  const myRole = currentUser?.role;
  const canEdit = (myRole === "admin" || myRole === "superadmin") && member.user_id !== currentUser?.user_id;
  const isTargetSuperadmin = member.role === "superadmin";
  const canEditSuperadmin = myRole === "superadmin";
  const canChange = canEdit && (canEditSuperadmin || !isTargetSuperadmin);

  const current = ROLE_OPTIONS.find(r => r.value === member.role) || { label: member.role, color: "#94a3b8" };

  const change = async (newRole) => {
    if (newRole === member.role) { setOpen(false); return; }
    if (newRole === "superadmin" && myRole !== "superadmin") {
      toast.error("Seul un superadmin peut promouvoir en superadmin");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/admin/users/${member.user_id}/role?role=${newRole}`);
      toast.success(`Role mis a jour : ${ROLE_OPTIONS.find(r => r.value === newRole)?.label}`);
      onUpdated();
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
    setSaving(false);
  };

  if (!canChange) {
    return (
      <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: `${current.color}15`, color: current.color }}>
        {current.label}
      </span>
    );
  }

  return (
    <div className="relative inline-block" data-testid={`role-selector-${member.user_id}`}>
      <button
        ref={triggerRef}
        onClick={() => setOpen(!open)}
        disabled={saving}
        className="text-xs px-2 py-0.5 rounded-full font-medium flex items-center gap-1 hover:ring-2 hover:ring-white/20 transition-all"
        style={{ background: `${current.color}15`, color: current.color }}
        data-testid={`role-btn-${member.user_id}`}
      >
        {current.label}
        <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
      </button>
      <PortalDropdown open={open} onClose={() => setOpen(false)} triggerRef={triggerRef} align="left" width={200}>
        <div className="py-1" data-testid={`role-menu-${member.user_id}`}>
          {ROLE_OPTIONS.filter(r => r.value !== "superadmin" || myRole === "superadmin").map(r => (
            <button
              key={r.value}
              onClick={() => change(r.value)}
              className="w-full text-left px-3 py-1.5 text-xs hover:bg-white/5 flex items-center gap-2"
              data-testid={`role-option-${r.value}`}
            >
              <span className="w-2 h-2 rounded-full" style={{ background: r.color }} />
              <span style={{ color: r.value === member.role ? r.color : "#cbd5e1" }} className={r.value === member.role ? "font-bold" : ""}>
                {r.label}
              </span>
              {r.value === member.role && <span className="ml-auto text-[#22c55e]">✓</span>}
            </button>
          ))}
        </div>
      </PortalDropdown>
    </div>
  );
}



// ─────────── ADDITIONAL ROLES PICKER ────────
// Roles secondaires : 6 roles metier + admin/superadmin (superadmin promotion = restricted)
const ADDITIONAL_ROLES = [
  { value: "formateur", label: "Formateur", color: "#8b5cf6", desc: "Acces espace formateur" },
  { value: "stagiaire", label: "Stagiaire", color: "#06b6d4", desc: "Acces espace stagiaire" },
  { value: "comptable", label: "Comptable", color: "#10b981", desc: "Acces module comptabilite" },
  { value: "coach", label: "Coach", color: "#a855f7", desc: "Acces accompagnements" },
  { value: "commercial", label: "Commercial", color: "#ec4899", desc: "Acces CRM et facturation" },
  { value: "sous_traitant", label: "Sous-traitant", color: "#f59e0b", desc: "Acces missions externes" },
];

function AdditionalRolesPicker({ member, api, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const triggerRef = useRef(null);
  const current = member.roles || [];

  const toggle = async (roleKey) => {
    const next = current.includes(roleKey)
      ? current.filter(r => r !== roleKey)
      : [...current, roleKey];
    setSaving(true);
    try {
      await api.put(`/admin/users/${member.user_id}/additional-roles`, { roles: next });
      toast.success(current.includes(roleKey) ? `${roleKey} retire` : `${roleKey} ajoute`);
      onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
    setSaving(false);
  };

  return (
    <div className="relative inline-flex items-center gap-1" data-testid={`additional-roles-${member.user_id}`}>
      {/* Existing additional roles displayed as badges */}
      {current.map(r => {
        const cfg = ADDITIONAL_ROLES.find(x => x.value === r);
        if (!cfg) return null;
        return (
          <span
            key={r}
            className="text-xs px-2 py-0.5 rounded-full font-medium uppercase"
            style={{ background: `${cfg.color}15`, color: cfg.color }}
            data-testid={`member-role-${r}`}
          >
            {cfg.label}
          </span>
        );
      })}
      {/* + button */}
      <button
        ref={triggerRef}
        onClick={() => setOpen(!open)}
        disabled={saving}
        className="text-[11px] w-5 h-5 rounded-full border border-dashed border-white/20 text-slate-500 hover:border-[#f7c935] hover:text-[#f7c935] transition-colors flex items-center justify-center"
        title="Ajouter un acces secondaire"
        data-testid={`add-role-btn-${member.user_id}`}
      >
        +
      </button>
      <PortalDropdown open={open} onClose={() => setOpen(false)} triggerRef={triggerRef} align="left" width={240}>
        <div data-testid={`additional-menu-${member.user_id}`}>
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-bold border-b border-white/5">
            Acces additionnels
          </div>
          {ADDITIONAL_ROLES.map(r => {
            const active = current.includes(r.value);
            return (
              <button
                key={r.value}
                onClick={() => toggle(r.value)}
                className="w-full text-left px-3 py-2 text-xs hover:bg-white/5 flex items-center gap-2"
                data-testid={`additional-option-${r.value}`}
              >
                <span
                  className="w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0"
                  style={{
                    background: active ? r.color : "transparent",
                    borderColor: active ? r.color : "rgba(255,255,255,0.2)",
                  }}
                >
                  {active && <span className="text-white text-[8px] font-bold">✓</span>}
                </span>
                <div className="flex-1">
                  <div className="font-bold" style={{ color: active ? r.color : "#fff" }}>{r.label}</div>
                  <div className="text-[10px] text-slate-500">{r.desc}</div>
                </div>
              </button>
            );
          })}
        </div>
      </PortalDropdown>
    </div>
  );
}
